import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Building } from 'lucide-react';
import { clinicApi } from '@features/clinics/services/clinicApi';
import apiClient from '@shared/services/apiClient';
import ConfirmationModal from '@shared/components/common/ConfirmationModal';
import type { ClinicWithDistance } from '@features/clinics/types/clinic';
import type { Contact } from '@features/clinics/types/contact';
import type { AccountStatus } from '../types/clientClinic';
import './ClientRelationshipPanel.css';

interface ClientRelationshipPanelProps {
  clinic: ClinicWithDistance;
  customerLocationId: number;
  onClose: () => void;
  onRelationshipChange: () => void;
}

interface ClinicLocationAccount {
  id: number;
  clinicId: number;
  customerLocationId: number;
  accountNumber?: string;
  accountStatus: AccountStatus;
  notes?: string;
}

export default function ClientRelationshipPanel({
  clinic,
  customerLocationId,
  onClose,
  onRelationshipChange,
}: Readonly<ClientRelationshipPanelProps>) {
  const queryClient = useQueryClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<AccountStatus | null>(null);
  const [showInactivateModal, setShowInactivateModal] = useState(false);
  const [inactivateReason, setInactivateReason] = useState('');

  // Fetch clinic contacts
  const { data: contacts = [] } = useQuery({
    queryKey: ['clinicContacts', clinic.id],
    queryFn: () => clinicApi.getClinicContacts(clinic.id),
  });

  const primaryContact = contacts.find((c: Contact) => c.isPrimary);

  // Fetch existing relationship
  const { data: relationships = [], isLoading: isLoadingRelationship } = useQuery<ClinicLocationAccount[]>({
    queryKey: ['clinicLocationAccounts', customerLocationId, clinic.id],
    queryFn: async () => {
      const response = await apiClient.get(`/clinic-location-client-accounts/location/${customerLocationId}`);
      return response.data;
    },
  });

  const existingRelationship = relationships.find(
    (r: ClinicLocationAccount) => r.clinicId === clinic.id
  );

  // Create relationship mutation
  const createMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: AccountStatus; notes?: string }) => {
      const response = await apiClient.post('/clinic-location-client-accounts', {
        clinicId: clinic.id,
        customerLocationId,
        accountStatus: status,
        ...(notes ? { notes } : {}),
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinicLocationAccounts'] });
      onRelationshipChange();
      setShowConfirmModal(false);
      setShowInactivateModal(false);
      setPendingStatus(null);
      setInactivateReason('');
    },
  });

  // Update relationship mutation
  const updateMutation = useMutation({
    mutationFn: async ({ status, notes }: { status: AccountStatus; notes?: string }) => {
      if (!existingRelationship) return;
      const response = await apiClient.put(
        `/clinic-location-client-accounts/${existingRelationship.id}`,
        {
          accountStatus: status,
          ...(notes ? { notes } : {}),
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinicLocationAccounts'] });
      onRelationshipChange();
      setShowConfirmModal(false);
      setShowInactivateModal(false);
      setPendingStatus(null);
      setInactivateReason('');
    },
  });

  const handleStatusChange = (status: AccountStatus) => {
    if (status === 'Inactive') {
      setInactivateReason('');
      setShowInactivateModal(true);
      return;
    }
    setPendingStatus(status);
    setShowConfirmModal(true);
  };

  const confirmStatusChange = () => {
    if (!pendingStatus) return;

    if (existingRelationship) {
      updateMutation.mutate({ status: pendingStatus });
    } else {
      createMutation.mutate({ status: pendingStatus });
    }
  };

  const confirmInactivate = () => {
    if (!inactivateReason.trim()) return;

    const existingNotes = existingRelationship?.notes;
    const notes = existingNotes
      ? `[Inactivated] ${inactivateReason.trim()}\n\n${existingNotes}`
      : `[Inactivated] ${inactivateReason.trim()}`;

    if (existingRelationship) {
      updateMutation.mutate({ status: 'Inactive', notes });
    } else {
      createMutation.mutate({ status: 'Inactive', notes });
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const currentStatus = existingRelationship?.accountStatus;

  const getStatusLabel = (status: AccountStatus) => {
    const labels: Record<AccountStatus, string> = {
      Pending: 'Mark as Pending',
      Active: 'Confirm Relationship',
      Inactive: 'Cancel Relationship',
    };
    return labels[status];
  };


  return (
    <div className="client-relationship-panel">
      <div className="panel-header">
        <h3>{clinic.name}</h3>
        <button
          type="button"
          className="panel-close"
          onClick={onClose}
          disabled={isLoading}
          aria-label="Close panel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="panel-body">
        {/* Clinic Info Section */}
        <section className="panel-section">
          <h4 className="section-title">
            <Building size={12} />
            Clinic Info
          </h4>
          <div className="section-content">
            <div className="info-item">
              <div className="info-label">Code</div>
              <div className="info-value">{clinic.code}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Location</div>
              <div className="info-value">{clinic.city}, {clinic.state}</div>
            </div>
            <div className="info-item">
              <div className="info-label">Distance</div>
              <div className="info-value">{clinic.distanceMiles != null ? `${clinic.distanceMiles.toFixed(1)} mi` : '—'}</div>
            </div>
            {clinic.phone && (
              <div className="info-item">
                <div className="info-label">Phone</div>
                <div className="info-value">
                  <a href={`tel:${clinic.phone}`}>{clinic.phone}</a>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Primary Contact Section */}
        {primaryContact && (
          <section className="panel-section">
            <h4 className="section-title">Contact</h4>
            <div className="section-content">
              <div className="info-item">
                <div className="info-label">Name</div>
                <div className="info-value">
                  {primaryContact.firstName} {primaryContact.lastName}
                </div>
              </div>
              {primaryContact.phone && (
                <div className="info-item">
                  <div className="info-label">Phone</div>
                  <div className="info-value">
                    <a href={`tel:${primaryContact.phone}`}>{primaryContact.phone}</a>
                  </div>
                </div>
              )}
              {primaryContact.email && (
                <div className="info-item">
                  <div className="info-label">Email</div>
                  <div className="info-value">
                    <a href={`mailto:${primaryContact.email}`}>{primaryContact.email}</a>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Relationship Status Section */}
        <section className="panel-section">
          <h4 className="section-title">Status</h4>
          <div className="section-content">
            {isLoadingRelationship ? (
              <div className="loading-message">Loading...</div>
            ) : existingRelationship ? (
              <>
                <div className="info-item">
                  <div className="info-label">Status</div>
                  <div className="info-value">
                    <span className={`status-badge status-${currentStatus?.toLowerCase()}`}>
                      {currentStatus}
                    </span>
                  </div>
                </div>
                {existingRelationship.accountNumber && (
                  <div className="info-item">
                    <div className="info-label">Account</div>
                    <div className="info-value">{existingRelationship.accountNumber}</div>
                  </div>
                )}
              </>
            ) : (
              <div className="no-relationship">No existing relationship</div>
            )}
          </div>
        </section>
      </div>

      {/* Action Buttons — horizontal row */}
      <div className="panel-footer">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => handleStatusChange('Inactive')}
          disabled={isLoading || currentStatus === 'Inactive'}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-warning"
          onClick={() => handleStatusChange('Pending')}
          disabled={isLoading || currentStatus === 'Pending'}
        >
          Pending
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => handleStatusChange('Active')}
          disabled={isLoading || currentStatus === 'Active'}
        >
          Confirm
        </button>
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        title="Confirm Status Change"
        message={`Are you sure you want to ${getStatusLabel(pendingStatus || 'Pending').toLowerCase()}?`}
        confirmText="Confirm"
        onConfirm={confirmStatusChange}
        onCancel={() => {
          setShowConfirmModal(false);
          setPendingStatus(null);
        }}
        loading={isLoading}
      />

      {showInactivateModal && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget && !isLoading) { setShowInactivateModal(false); setInactivateReason(''); } }}>
          <div className="modal">
            <div className="modal-header">
              <h2>Inactivate Relationship</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => { setShowInactivateModal(false); setInactivateReason(''); }}
                disabled={isLoading}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to inactivate the relationship with <strong>{clinic.name}</strong>?</p>
              <div className="form-group">
                <label htmlFor="inactivate-reason">
                  Reason for inactivation<span className="required">*</span>
                </label>
                <textarea
                  id="inactivate-reason"
                  value={inactivateReason}
                  onChange={(e) => setInactivateReason(e.target.value)}
                  placeholder="Enter the reason this relationship is being inactivated..."
                  rows={3}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setShowInactivateModal(false); setInactivateReason(''); }}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmInactivate}
                disabled={isLoading || !inactivateReason.trim()}
              >
                {isLoading ? 'Saving...' : 'Inactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
